import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { KnobModule } from 'primeng/knob';
import { ButtonsTemperatureSetPoint, TH_MANUAL_TEMP_STEP } from 'src/app/conf/thermo';
import { PermittedAccessThs } from 'src/app/model/permission';
import { Thermo } from 'src/app/model/thermo';
import { StandardPageLayout } from '../../stdlayoutlib/classes.stdlayoutlib';

@Component({
	selector: 'app-thermo-bnb-manual-control',
	standalone: true,
	imports: [CommonModule, FormsModule, TranslateModule, ButtonModule, KnobModule],
	templateUrl: './thermo-bnb-manual-control.component.html'
})
export class ThermoBnbManualControlComponent implements OnInit {
	@Input() thermoInput: Thermo | PermittedAccessThs;
	@Input() camelCaseFormat: boolean | undefined;

	@Output() tempChange = new EventEmitter<number>();

	buttonsTempSet = ButtonsTemperatureSetPoint;

	screenWidth: number;

	ngOnInit(): void {
		this.onResize();
	}

	get mode(): string | undefined {
		if (!this.thermoInput) {
			return undefined;
		}
		return this.camelCaseFormat
			? (this.thermoInput as PermittedAccessThs).Mode
			: (this.thermoInput as Thermo).mode;
	}

	get minTemp(): number | undefined {
		if (this.camelCaseFormat) {
			return (this.thermoInput as PermittedAccessThs)?.Bb?.thManualTempMin;
		}
		const bb = (this.thermoInput as Thermo)?.bb;
		if (bb?.temp == null || bb?.delta_temp_n == null) {
			return undefined;
		}
		return this.getValueFloat(bb.temp - bb.delta_temp_n);
	}

	get maxTemp(): number | undefined {
		if (this.camelCaseFormat) {
			return (this.thermoInput as PermittedAccessThs)?.Bb?.thManualTempMax;
		}
		const bb = (this.thermoInput as Thermo)?.bb;
		if (bb?.temp == null || bb?.delta_temp_p == null) {
			return undefined;
		}
		return this.getValueFloat(bb.temp + bb.delta_temp_p);
	}

	get step(): number | undefined {
		if (this.camelCaseFormat) {
			return (this.thermoInput as PermittedAccessThs)?.Bb?.thManualTempStepsValue;
		}
		return (this.thermoInput as Thermo)?.bb ? TH_MANUAL_TEMP_STEP : undefined;
	}

	get hasQe(): boolean {
		if (!this.thermoInput) {
			return false;
		}
		return this.camelCaseFormat
			? !!(this.thermoInput as PermittedAccessThs).Qe
			: !!(this.thermoInput as Thermo).qe;
	}

	get showKnob(): boolean {
		return this.minTemp != null && this.maxTemp != null && this.step != null && this.hasQe;
	}

	get manualTemp(): number | undefined {
		if (!this.thermoInput) {
			return undefined;
		}
		return this.camelCaseFormat
			? (this.thermoInput as PermittedAccessThs).ManualTemp
			: (this.thermoInput as Thermo).manual_temp;
	}

	set manualTemp(value: number) {
		if (!this.thermoInput) {
			return;
		}
		if (this.camelCaseFormat) {
			(this.thermoInput as PermittedAccessThs).ManualTemp = value;
		} else {
			(this.thermoInput as Thermo).manual_temp = value;
		}
	}

	onKnobChange(eventValue: number): void {
		this.emitTempChange(eventValue);
	}

	setSliderButtonValue(buttonsTempSet: ButtonsTemperatureSetPoint): void {
		const current = this.manualTemp ?? 0;
		const next = buttonsTempSet === ButtonsTemperatureSetPoint.INCREASE
			? current + TH_MANUAL_TEMP_STEP
			: current - TH_MANUAL_TEMP_STEP;
		this.emitTempChange(next);
	}

	private emitTempChange(eventValue: number): void {
		const valueFixed = this.getValueFloat(eventValue);
		this.manualTemp = valueFixed;
		this.tempChange.emit(valueFixed);
	}

	private getValueFloat(value: number): number {
		return parseFloat(value.toFixed(1));
	}

	@HostListener('window:resize')
	onResize(): void {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}
}
